import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  alpha,
  useTheme,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

export const ACCENT = '#137274';
export const PINK = '#f50057';

export const useAdminPageTheme = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return {
    isDark,
    border: isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)',
    surface: isDark ? '#161b22' : '#ffffff',
    muted: isDark ? '#8b949e' : '#5f6b73',
    pageBg: isDark
      ? '#0f1419'
      : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)',
  };
};

export const difficultyColor = (difficulty) => {
  const d = String(difficulty || '').toLowerCase();
  if (d === 'easy') return '#1a7f37';
  if (d === 'medium') return '#d97706';
  if (d === 'hard') return '#cf222e';
  return '#8b949e';
};

export const roleColor = (role) => {
  const r = String(role || '').toLowerCase();
  if (r === 'admin') return '#cf222e';
  if (r === 'softadmin') return '#d97706';
  return ACCENT;
};

export const AdminPage = ({ children, maxWidth = 1200 }) => {
  const { pageBg } = useAdminPageTheme();
  return (
    <Box sx={{ minHeight: '100vh', background: pageBg, px: { xs: 1.5, sm: 2.5 }, py: 2.5 }}>
      <Box sx={{ maxWidth, mx: 'auto' }}>{children}</Box>
    </Box>
  );
};

export const AdminPageHeader = ({ title, subtitle, actions }) => {
  const { muted } = useAdminPageTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1.5,
        mb: 2.5,
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 900, fontSize: '1.4rem', letterSpacing: '-0.03em' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: muted, mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {actions}
        </Box>
      ) : null}
    </Box>
  );
};

export const AdminStatGrid = ({ items }) => {
  const { border, surface, muted, isDark } = useAdminPageTheme();
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${Math.min(items.length, 4)}, 1fr)` },
        gap: 1.25,
        mb: 2.5,
      }}
    >
      {items.map((item) => (
        <Box
          key={item.label}
          sx={{
            p: 1.75,
            borderRadius: 2,
            border: `1px solid ${border}`,
            bgcolor: surface,
          }}
        >
          <Typography variant="caption" sx={{ color: muted, fontWeight: 700, letterSpacing: '0.04em' }}>
            {item.label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: '1.35rem',
              letterSpacing: '-0.03em',
              color: item.color || (isDark ? '#e6edf3' : '#0f2a2b'),
              mt: 0.25,
            }}
          >
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const AdminToolbar = ({ children }) => {
  const { border, surface } = useAdminPageTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.5,
        mb: 2.5,
        p: 1.75,
        borderRadius: 2,
        border: `1px solid ${border}`,
        bgcolor: surface,
        alignItems: 'center',
      }}
    >
      {children}
    </Box>
  );
};

export const AdminCard = ({ children, sx = {} }) => {
  const { border, surface } = useAdminPageTheme();
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: `1px solid ${border}`,
        bgcolor: surface,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export const AdminEmpty = ({ title, description }) => {
  const { border, surface, muted } = useAdminPageTheme();
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 8,
        borderRadius: 2.5,
        border: `1px dashed ${border}`,
        bgcolor: surface,
      }}
    >
      <Typography sx={{ fontWeight: 800, mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: muted }}>
        {description}
      </Typography>
    </Box>
  );
};

export const AdminLoading = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
    <CircularProgress sx={{ color: ACCENT }} />
  </Box>
);

export const MetaChip = ({ label, color = ACCENT, isDark }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 22,
      px: 0.85,
      borderRadius: 1,
      fontSize: '0.68rem',
      fontWeight: 800,
      textTransform: 'capitalize',
      bgcolor: alpha(color, isDark ? 0.22 : 0.1),
      color,
    }}
  >
    {label}
  </Box>
);

/** Consistent field styling inside admin forms */
export const adminFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
  },
};

export const AdminFormSection = ({ title, description, children }) => {
  const { muted, border, surface } = useAdminPageTheme();
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${border}`,
        bgcolor: surface,
        mb: 2,
      }}
    >
      {title && (
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', letterSpacing: '-0.01em', mb: 0.35 }}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body2" sx={{ color: muted, fontSize: '0.8rem', mb: 1.5 }}>
          {description}
        </Typography>
      )}
      {!description && title && <Box sx={{ mb: 1.25 }} />}
      {children}
    </Box>
  );
};

export const AdminFormDialog = ({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  formId,
  submitLabel = 'Save',
  loading = false,
  maxWidth = 'md',
  children,
}) => {
  const { border, surface, muted, isDark } = useAdminPageTheme();

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${border}`,
          bgcolor: isDark ? '#0d1117' : '#f6f8fa',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          borderBottom: `1px solid ${border}`,
          bgcolor: surface,
        }}
      >
        {Icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1),
              color: ACCENT,
            }}
          >
            <Icon sx={{ fontSize: 22 }} />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0, pt: Icon ? 0.15 : 0 }}>
          <Typography sx={{ fontWeight: 900, fontSize: '1.05rem', letterSpacing: '-0.02em' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: muted, mt: 0.35, fontSize: '0.82rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={loading}
          sx={{ color: muted, mt: -0.25 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 2.5, bgcolor: isDark ? '#0d1117' : '#f6f8fa' }}>
        {children}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.75,
          gap: 1,
          borderTop: `1px solid ${border}`,
          bgcolor: surface,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 700, color: muted }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form={formId}
          variant="contained"
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            minWidth: 120,
            bgcolor: ACCENT,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
